import { memo, useCallback, useEffect } from 'react';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useTranslation } from 'react-i18next';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DashboardRow } from './row/DashboardRow';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { Button } from 'components/mui/common/Button';
import { PlaylistAdd, Delete } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useBooleanState } from 'utils/hooks';
import { AddDashboardRowModal } from './row/AddDashboardRowModal';
import type { DashboardItem } from 'types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDashboardSchema } from 'utils/validations/useDashboardSchema';

interface Props {
  open: boolean;
  onClose: () => void;
  onRemove?: () => void;
  onSubmit: (data: FormValues) => void;
  isModule?: boolean;
  isFlow?: boolean;
  isHelios?: boolean;
  data?: DashboardItem[];
  showCounts?: boolean;
}

export interface FormValues {
  dashboard: DashboardItem[];
  showCounts?: boolean;
}

const DashboardBase = ({ open, onClose, onSubmit, onRemove, isModule, isFlow = true, isHelios = true, data, showCounts }: Props) => {
  const { t } = useTranslation();
  const dashboardSchema = useDashboardSchema();
  const form = useForm<FormValues>({
    defaultValues: {
      dashboard: [],
      showCounts: showCounts ?? true,
    },
    resolver: yupResolver(dashboardSchema),
  });

  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = form;
  const [addModal, toggleAddModal] = useBooleanState(false);
  const { fields, swap, remove, append } = useFieldArray({
    control,
    name: 'dashboard',
  });

  const handleDroppedItem = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = fields.findIndex((item) => item.id === active.id);
      const newIndex = fields.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        swap(Number(oldIndex), Number(newIndex));

        setValue(`dashboard.${oldIndex}.row`, Number(oldIndex) + 1, { shouldDirty: true });
        setValue(`dashboard.${newIndex}.row`, Number(newIndex) + 1, { shouldDirty: true });
      }
    },
    [fields, swap, setValue]
  );

  useEffect(() => {
    if (data) {
      reset({ dashboard: data, showCounts });
    }
  }, [data, reset, showCounts]);

  const onFormSubmit = (values: FormValues) => {
    onSubmit(values);
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      disableBackdropClick
      title={t('core.settings.dashboard.title')}
      maxWidth="xl"
      submitText={t('common.save')}
      onCancel={onClose}
      onSubmit={handleSubmit(onFormSubmit)}
      isSubmitting={isSubmitting}>
      <FormProvider {...form}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px', gap: '10px' }}>
          <Button startIcon={<PlaylistAdd />} type="button" variant="outlined" onClick={toggleAddModal}>
            {t('common.addRow')}
          </Button>
          {onRemove && (
            <Button startIcon={<Delete />} variant="contained" onClick={onRemove}>
              {t('core.settings.dashboard.deleteDashboard')}
            </Button>
          )}
        </Box>

        <DndContext onDragEnd={handleDroppedItem}>
          <SortableContext items={fields.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {fields.map((row, index) => (
                <DashboardRow
                  key={row.id}
                  index={index}
                  id={row.id}
                  type={row.type}
                  onRemove={remove}
                  isModule={isModule}
                  isFlow={isFlow}
                  isHelios={isHelios}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {addModal && <AddDashboardRowModal open={addModal} onClose={toggleAddModal} onAdd={append} length={fields.length} isModule={isModule} />}
      </FormProvider>
    </ActionModal>
  );
};

export const Dashboard = memo(DashboardBase) as typeof DashboardBase;
